import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { API, useAuth } from '../../App';
import { useHubT } from './hubStrings';
import { Button } from '../ui/button';
import CommunityReportCard from './CommunityReportCard';
import ReportComposer from './ReportComposer';

export const CommunityReportsFeed = ({ incidentId = null, defaultTown = '', locked = false, helpFilter = null, composerCategory = 'general_update' }) => {
  const hubT = useHubT();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [expiredReports, setExpiredReports] = useState([]);
  const [showExpired, setShowExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { includeExpired: true };
      if (incidentId) params.incidentId = incidentId;
      if (helpFilter) params.help = helpFilter;
      const r = await axios.get(`${API}/community-reports`, {
        params,
        headers: user?.token ? { Authorization: `Bearer ${user.token}` } : {},
      });
      setReports(r.data.reports || []);
      setExpiredReports(r.data.expiredReports || []);
    } catch { /* non-fatal */ } finally {
      setLoading(false);
    }
  }, [incidentId, helpFilter, user?.token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="community-reports-feed">
      {!locked && (
        <div className="flex justify-end mb-3">
          <Button
            size="sm"
            onClick={() => setComposerOpen(true)}
            className="bg-slate-800 hover:bg-slate-900 text-white"
            data-testid="community-new-report-btn"
          >
            <Plus className="h-4 w-4 mr-1" />{hubT('newReport')}
          </Button>
        </div>
      )}
      {locked && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">{hubT('locked')}</p>
      )}
      {loading ? (
        <div className="text-center text-gray-400 py-8">…</div>
      ) : reports.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6" data-testid="community-reports-empty">
          {helpFilter ? hubT('noHelpPosts') : hubT('noReports')}
        </p>
      ) : (
        <div className="space-y-3">
          {reports.map(r => <CommunityReportCard key={r.id} report={r} onChanged={load} />)}
        </div>
      )}

      {expiredReports.length > 0 && (
        <div className="mt-5">
          <button
            onClick={() => setShowExpired(s => !s)}
            className="text-xs text-gray-500 underline"
            data-testid="community-toggle-expired"
          >
            {showExpired ? hubT('hideExpired') : `${hubT('showExpired')} (${expiredReports.length})`}
          </button>
          {showExpired && (
            <div className="space-y-3 mt-3" data-testid="community-expired-section">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{hubT('expiredUpdates')}</p>
              {expiredReports.map(r => <CommunityReportCard key={r.id} report={r} onChanged={load} />)}
            </div>
          )}
        </div>
      )}

      <ReportComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        incidentId={incidentId}
        defaultTown={defaultTown}
        defaultCategory={composerCategory}
        onPosted={load}
      />
    </div>
  );
};

export default CommunityReportsFeed;
